import * as path from 'path';
import * as Puppeteer from 'puppeteer';
import { GhostCursor } from 'ghost-cursor';

import type { Page } from '../..';
import { Plugin } from '..';
import { AvoidDetectionPlugin } from './../avoid.detection';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const injection = require(path.resolve(`${__dirname}/injections`) + '/utils.js');
const randomBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min)) + min;

export class SolveRecaptchasPlugin extends Plugin {
  dependencies = [new AvoidDetectionPlugin()];
  witAiAccessToken?: string;

  constructor(witAiAccessToken: string) {
    super();
    this.witAiAccessToken = witAiAccessToken;
  }

  async waitForCaptcha(page: Page, timeout?: number) {
    return page.waitForFunction(() => !!document.querySelector<HTMLIFrameElement>('iframe[src*="api2/anchor"]') &&
      !!document.querySelector<HTMLIFrameElement>('iframe[src*="api2/bframe"]'), { timeout });
  }

  async hasCaptcha(page: Page) {
    return page.evaluate(() => !!document.querySelector<HTMLIFrameElement>('iframe[src*="api2/anchor"]') &&
      !!document.querySelector<HTMLIFrameElement>('iframe[src*="api2/bframe"]'));
  }

  async isCaptchaSolved(page: Page) {
    const anchorFrame = page.frames().find(x => x.url().includes('api2/anchor'));
    if (!anchorFrame) return false;
    return anchorFrame.evaluate(() => !!document.querySelector('.recaptcha-checkbox-checked'));
  }

  async solveRecaptcha(page: Page) {
    if (this.isStopped) return;
    if (!this.witAiAccessToken) return;
    if (!(await this.hasCaptcha(page))) return;

    const anchorFrame = page.frames().find(x => x.url().includes('api2/anchor'));
    if (!anchorFrame) return;

    const cursor = new GhostCursor(page);

    async function waitForSelector(iframe: Puppeteer.Frame, selector: string) {
      await iframe.waitForFunction((_selector: string) => document.querySelector(_selector), {}, selector);
    }

    async function findAndClick(iframe: Puppeteer.Frame, selector: string) {
      await waitForSelector(iframe, selector);

      const element = await iframe.$(selector);
      if (!element) return;

      await sleep(randomBetween(1 * 1000, 3 * 1000));
      await cursor.click(element);
    }

    const isSolved = async () => {
      if (!(await this.hasCaptcha(page))) return true;
      return !!(await anchorFrame.evaluate(() => !!document.querySelector('.recaptcha-checkbox-checked')));
    };

    await findAndClick(anchorFrame, '#recaptcha-anchor');

    const bframeFrame = page.frames().find(x => x.url().includes('api2/bframe'));
    if (!bframeFrame) return;

    await findAndClick(bframeFrame, '.rc-button-audio');

    const maxAttempts = 5;
    for (let attempt = 0; !(await isSolved()); attempt++) {
      if (attempt >= maxAttempts) {
        throw new Error(`reCAPTCHA solve failed after ${maxAttempts} attempts`);
      }

      await waitForSelector(bframeFrame, '.rc-audiochallenge-tdownload-link');

      const audioUrl = await bframeFrame.evaluate(async () => document.querySelector<HTMLLinkElement>('.rc-audiochallenge-tdownload-link')?.href);
      if (!audioUrl) return;

      const audioArray = await bframeFrame.evaluate(injection, audioUrl);
      if (!audioArray) return;

      const audioBuffer = Buffer.from(new Int8Array(audioArray));

      const response = await fetch('https://api.wit.ai/speech?v=20220527', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.witAiAccessToken}`,
          'Content-Type': 'audio/wav',
        },
        body: audioBuffer,
      });

      if (!response.ok) {
        throw new Error(`Wit.ai API error: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();

      const data = JSON.parse(text.split('\r\n').filter(line => line.trim()).slice(-1)[0] || '{}');

      if (data?.text) {
        const responseInput = await bframeFrame.$('#audio-response');
        await responseInput?.type(data.text);

        await findAndClick(bframeFrame, '#recaptcha-verify-button');

        await sleep(1000);
      } else {
        await findAndClick(bframeFrame, '#recaptcha-reload-button');
      }
    }
  }
}

function sleep(timeout: number) {
  return new Promise(resolve => setTimeout(resolve, timeout));
}
